import { useState } from "react";

/**
 * Versioned-dismissal hook for localStorage-backed dismissible UI elements.
 *
 * Pass a storage key that includes a `_v<N>` suffix (e.g. `"hideMyBanner_v1"`).
 * Bumping the version number resets the dismissed state for all users.
 *
 * @param storageKey  Versioned localStorage key, e.g. "hideMyBanner_v1"
 * @param resetParam  Optional URL search param that forces the banner visible (defaults to "showBanner")
 */
export function useDismissible(
  storageKey: string,
  resetParam = "showBanner",
): {
  visible: boolean;
  dismissed: boolean;
  dismiss: () => void;
  onAnimationComplete: () => void;
} {
  const alreadyDismissed = (() => {
    try {
      if (new URLSearchParams(window.location.search).get(resetParam) === "1") {
        localStorage.removeItem(storageKey);
        return false;
      }
      return localStorage.getItem(storageKey) === "true";
    } catch {
      return false;
    }
  })();

  const [visible, setVisible] = useState<boolean>(!alreadyDismissed);
  const [dismissed, setDismissed] = useState<boolean>(alreadyDismissed);

  const dismiss = () => {
    try {
      localStorage.setItem(storageKey, "true");
    } catch {
      // Persist failure is non-fatal — the banner will re-appear on next load
    }
    setVisible(false);
  };

  const onAnimationComplete = () => {
    if (!visible) setDismissed(true);
  };

  return { visible, dismissed, dismiss, onAnimationComplete };
}
